import { Link, Logo } from '@checkatrade/web-ui';
import imageUrlBuilder from '@utils/image-url-builder';
import { PaymentBadge } from 'components/@pages/CompanyPage/payment-badge';
import { Stack } from 'components/layout/stack';
import type { HeadingTags } from 'lib/design-system';
import { IconOpenLink } from 'lib/design-system';
import { HeadingXS, HeadingM, IconLocationPinFill } from 'lib/design-system';
import { TradeRating } from 'lib/job/components/trade-rating';
import {
  borderRadius,
  default200,
  default700,
  fontSizeBodySM,
  fontSizeLabelsRegular,
  fontWeightSemibold,
  accent90,
  neutral600,
  neutral900,
  spacing1,
  spacing2,
  spacing3,
} from 'lib/mortar';
import { type ReactNode, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { tv } from 'tailwind-variants';
import { colors } from '@checkatrade/web-ui/themes';
import { Experiment, useOptimizely } from 'lib/sdks/optimizely';
import { track } from 'lib/sdks/analytics';
import { Action } from 'lib/sdks/ad-server';

const CompanyInfoInner = styled.div<StyledDiv>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacing1};

  ${HeadingXS} {
    color: ${neutral900};
    font-size: ${fontSizeBodySM};
  }
`;

export const CompanyInfoContainer = styled(Stack)`
  padding: ${spacing3} 0;
`;

export const ReviewsPill = styled.span<StyledSpan>`
  display: flex;
  align-items: center;
  gap: ${spacing1};

  width: fit-content;
  padding: ${spacing1} ${spacing2};

  border-radius: ${borderRadius()};

  font-size: ${fontSizeBodySM};
  font-weight: ${fontWeightSemibold};

  color: ${default700};
  background-color: ${default200};

  span:last-child {
    color: ${neutral600};
    font-size: ${fontSizeLabelsRegular};
  }
`;

const SponsoredLabel = styled.p`
  font-size: ${fontSizeBodySM};
  color: ${neutral600};
`;

const IconContainer = styled.span<StyledSpan>`
  @media (hover: hover) {
    &:hover {
      path {
        fill: ${colors.accent['90']};
      }
    }
  }
`;

const addressStyle = tv({
  base: 'flex items-center gap-1 text-xs text-accent-90',
});

export const LocationPin = ({
  children,
  className,
}: {
  className?: string;
  children: ReactNode;
}) => (
  <address className={addressStyle({ className: className })}>
    <IconLocationPinFill size="small" color={accent90} />
    {children}
  </address>
);

export const CompanyInfo = ({
  name,
  logoUrl,
  reviewsScore,
  companyId,
  numberOfReviews,
  postcode,
  categoryId,
  showReviews = true,
  headingAs = 'span',
  payment,
  logoSize = 'md',
  uniqueName,
  onSponsoredClick,
}: {
  name: string;
  logoUrl?: string;
  reviewsScore: number | null;
  companyId?: number;
  numberOfReviews: number | null;
  postcode?: string;
  categoryId?: string;
  showReviews?: boolean;
  headingAs?: HeadingTags;
  payment?: { status?: string };
  logoSize?: 'sm' | 'md';
  uniqueName?: string;
  onSponsoredClick?: (action: Action) => void;
}) => {
  const logoUrlFormatted = logoUrl
    ? imageUrlBuilder(logoUrl, {
        width: 150,
        format: 'webp',
        cacheLifetime: 365,
        compressionLevel: 1,
      })
    : '';
  const logoAlt = `${name} Logo`;
  const { decide, loading: optimizelyLoading } = useOptimizely();
  const consumerGuidanceBannerEnabled = useMemo(() => {
    if (optimizelyLoading) return false;
    const decision = decide(Experiment.CONSUMER_GUIDANCE_BANNER);
    return decision?.enabled && decision?.variationKey === 'on';
  }, [optimizelyLoading, decide]);

  const onClick = useCallback(() => {
    track('raq_after_message.open_trade_profile_in_new_tab', {
      company_id: companyId,
      category_id: categoryId,
      location: postcode,
    });

    // Handle sponsored click if this is a sponsored trade
    if (onSponsoredClick) {
      onSponsoredClick(Action.PROFILE);
    }
  }, [companyId, categoryId, postcode, onSponsoredClick]);

  return (
    <>
      <Logo
        src={logoUrlFormatted}
        className={{ image: 'mr-2', initialsContainer: 'mr-2' }}
        alt={logoAlt}
        size={logoSize}
      />
      <CompanyInfoInner>
        {consumerGuidanceBannerEnabled ? (
          <HeadingM as={headingAs} color="tertiary">
            {name}
          </HeadingM>
        ) : (
          <HeadingXS as={headingAs} color="secondary">
            {name}
          </HeadingXS>
        )}
        {showReviews && (
          <TradeRating
            count={numberOfReviews as number}
            score={reviewsScore as number}
          />
        )}
        {postcode && <LocationPin>Operates in {postcode}</LocationPin>}
        {onSponsoredClick && <SponsoredLabel>Sponsored</SponsoredLabel>}
        <PaymentBadge payment={payment} location="add_more_trades" />
      </CompanyInfoInner>
      {consumerGuidanceBannerEnabled && uniqueName ? (
        <Link
          href={`/trades/${uniqueName}#categoryId=${categoryId}&location=${postcode}`}
          rel="nofollow"
          target="_blank"
          onClick={onClick}
          className="ml-auto pr-4"
        >
          <IconContainer>
            <IconOpenLink color={colors.accent.medium} size="medium" />
          </IconContainer>
        </Link>
      ) : null}
    </>
  );
};
